import { JsonObject, JsonProperty } from "typescript-json-serializer";
import {SpatialReferenceConfiguration} from "./spatial-reference-configuration";
import {GeometryConfiguration, ExtentConfiguration, PointConfiguration} from "./geometry-configuration";
import {ColorConfiguration, ColorBackgroundConfiguration} from "./color-configuration";
import {MapConfiguration} from "./map-configuration";
import {LODConfiguration} from "./lod-configuration";
import {PopupConfiguration} from "./popup-configuration";
import {TimeExtentConfiguration} from "./time-info-configration";
import Collection from "@arcgis/core/core/Collection";
@JsonObject()
export class ThemeConfiguration {
    /**
     * The base color used to render temporary graphics in the [View](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-Theme.html#accentColor)
     */
    @JsonProperty()
    declare accentColor?: ColorConfiguration | number[] | string;
    /**
     * The base color used to render temporary labels in the [View](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-Theme.html#textColor)
     */
    @JsonProperty()
    declare textColor?: ColorConfiguration | number[] | string;
}
@JsonObject()
export class ViewConfiguration {
    /** --- from DOMContainer ---*/

    /**
     * The `id` or node representing the DOM element containing the view.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-DOMContainer.html#container)
     */
    @JsonProperty()
    declare container: HTMLDivElement;

    /**
     * Indicates if the browser focus is on the view.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-DOMContainer.html#focused)
     */
    @JsonProperty()
    declare focused: boolean;
    /**
     * The height of the view in pixels read from the view container element.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-DOMContainer.html#height)
     */
    @JsonProperty()
    declare height: number;


    /**
     * Exposes the default widgets available in the view and allows you to toggle them on and off.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-DOMContainer.html#ui)
     */
    // @JsonProperty()
    // ui: DefaultUI;

    /**
     * The width of the view in pixels read from the view container element.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-DOMContainer.html#width)
     */
    @JsonProperty()
    declare width: number;

    /** --- end from DOMContainer ---*/

    /**
     * Collection containing a flat list of all the created [LayerViews](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-layers-LayerView.html) related to the basemap, operational layers, and group layers in this view.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html#allLayerViews)
     */
    // @JsonProperty()
    // declare allLayerViews: Collection<LayerView>;
    /**
     * Represents an ongoing view animation initialized by [goTo()](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#goTo).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html#animation)
     */
    // @JsonProperty()
    // declare animation: ViewAnimation;
    /**
     * Represents the view for a single basemap after it has been added to the map.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html#basemapView)
     */
    // declare basemapView: BasemapView;
    /**
     * A fatal [error](https://developers.arcgis.com/javascript/latest/api-reference/esri-core-Error.html) returned when the view loses its WebGL context.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html#fatalError)
     */
    // fatalError: Error;
    /**
     * Allows for adding [graphics](https://developers.arcgis.com/javascript/latest/api-reference/esri-Graphic.html) directly to the default graphics in the View.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html#graphics)
     */
    // @JsonProperty()
    // declare graphics: Collection<GraphicConfiguration>;

    /**
     * A collection containing a hierarchical list of all the created [LayerViews](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-layers-LayerView.html) of the [operational layers](https://developers.arcgis.com/javascript/latest/api-reference/esri-Map.html#layers) in the map.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html#layerViews)
     */
    // @JsonProperty()
    // declare layerViews: Collection<LayerView>;
    /**
     * An instance of a [Map](https://developers.arcgis.com/javascript/latest/api-reference/esri-Map.html) object to display in the view.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html#map)
     */
    @JsonProperty()
    declare map: MapConfiguration;

    /**
     * Use the padding property to make the [center](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#center), and [extent](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#extent), etc.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html#padding)
     */
    // @JsonProperty()
    // declare navigation: NavigationConfiguration;

    /**
     * Use the padding property to make the [center](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#center), and [extent](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#extent), etc.
     *
     * @default {left: 0, top: 0, right: 0, bottom: 0}
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html#padding)
     */
    // @JsonProperty()
    // declare padding: ViewPadding;

    /**
     * The spatial reference of the view.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html#spatialReference)
     */
    @JsonProperty()
    declare spatialReference: SpatialReferenceConfiguration;

    /**
     * This property specifies the base colors used by some widgets and components to render graphics and labels.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html#theme)
     */
    @JsonProperty()
    declare theme?: ThemeConfiguration;

    /**
     * The view's time extent.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html#timeExtent)
     */
    @JsonProperty()
    declare timeExtent: TimeExtentConfiguration;
    /** --- from PopupViewProperties --- */
    /**
     * A [Popup](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html) object that displays general content or attributes from [layers](https://developers.arcgis.com/javascript/latest/api-reference/esri-Map.html#layers) in the [map](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-PopupView.html#map).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-PopupView.html#popup)
     */
    @JsonProperty()
    declare popup?: PopupConfiguration;
    /**
     * Controls whether the popup opens when users click on the view.
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-PopupView.html#popupEnabled)
     */
    @JsonProperty()
    declare popupEnabled?: boolean;
    /** --- end from PopupViewProperties --- */
    constructor() {}
}

@JsonObject()
export class MapViewConstraintsConfiguration {
    /**
     * An array of [LODs](https://developers.arcgis.com/javascript/latest/api-reference/esri-layers-support-LOD.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#constraints)
     */
    @JsonProperty()
    declare lods?: LODConfiguration[];
    /**
     * The area in which the user is allowed to navigate laterally.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#constraints)
     */
    @JsonProperty()
    declare geometry?: GeometryConfiguration;
    /**
     * The minimum [scale](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#scale) the user is allowed to zoom to within the view.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#constraints)
     */
    @JsonProperty()
    declare minScale?: number;
    /**
     * The maximum [scale](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#scale) the user is allowed to zoom to within the view.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#constraints)
     */
    @JsonProperty()
    declare maxScale?: number;
    /**
     * The minimum [zoom](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#zoom) level the user is allowed to zoom to within the view.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#constraints)
     */
    @JsonProperty()
    declare minZoom?: number;
    /**
     * The maximum [zoom](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#zoom) level the user is allowed to zoom to within the view.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#constraints)
     */
    @JsonProperty()
    declare maxZoom?: number;
    /**
     * When `true`, the view snaps to the next LOD when zooming in or out.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#constraints)
     */
    @JsonProperty()
    declare snapToZoom?: boolean;
    /**
     * Indicates whether the user can rotate the map.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#constraints)
     */
    @JsonProperty()
    declare rotationEnabled?: boolean;
    /**
     * A read-only property that specifies the levels of detail (LODs) read from the [Map](https://developers.arcgis.com/javascript/latest/api-reference/esri-Map.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#constraints)
     */
    @JsonProperty()
    declare effectiveLODs?: LODConfiguration[];
    /**
     * A read-only property that specifies the minimum [zoom](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#zoom) level the user is allowed to zoom to within the view.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#constraints)
     */
    @JsonProperty()
    declare effectiveMinZoom?: number;
    /**
     * A read-only property that specifies the maximum [zoom](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#zoom) level the user is allowed to zoom to within the view.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#constraints)
     */
    @JsonProperty()
    declare effectiveMaxZoom?: number;
    /**
     * A read-only property that specifies the minimum [scale](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#scale) the user is allowed to zoom to within the view.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#constraints)
     */
    @JsonProperty()
    declare effectiveMinScale?: number;
    /**
     * A read-only property that specifies the maximum [scale](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#scale) the user is allowed to zoom to within the view.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#constraints)
     */
    @JsonProperty()
    declare effectiveMaxScale?: number;

    constructor() {}
}
@JsonObject()
export class CameraLayoutConfiguration {
    /**
     * The active column a display client renders to in a tiled display setup.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-CameraLayout.html#column)
     */
    @JsonProperty()
    declare column?: number;
    /**
     * The number of columns to decompose the camera in a tiled display setup.
     *
     * @default 1
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-CameraLayout.html#columns)
     */
    @JsonProperty()
    declare columns?: number;
    /**
     * The active row a display client renders to in a tiled display setup.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-CameraLayout.html#row)
     */
    @JsonProperty()
    declare row?: number;
    /**
     * The number of rows to decompose the camera in a tiled display setup.
     *
     * @default 1
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-CameraLayout.html#rows)
     */
    @JsonProperty()
    declare rows?: number;
}
@JsonObject()
export class CameraConfiguration {
    /**
     * The diagonal field of view (fov) angle for the camera.
     *
     * @default 55
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-Camera.html#fov)
     */
    @JsonProperty()
    declare fov?: number;
    /**
     * The compass heading of the camera in degrees.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-Camera.html#heading)
     */
    @JsonProperty()
    declare heading?: number;
    /**
     * The layout defines which sub-region of the camera is rendered.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-Camera.html#layout)
     */
    @JsonProperty()
    declare layout?: CameraLayoutConfiguration;
    /**
     * The position of the camera defined by a map point.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-Camera.html#position)
     */
    @JsonProperty()
    declare position?: PointConfiguration;
    /**
     * The tilt of the camera in degrees with respect to the surface as projected down from the camera position.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-Camera.html#tilt)
     */
    @JsonProperty()
    declare tilt?: number;

    constructor() {}
}

@JsonObject()
export class ViewpointConfiguration {
    /**
     * The viewpoint camera (3D only).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-Viewpoint.html#camera)
     */
    @JsonProperty()
    declare camera?: CameraConfiguration;
    /**
     * The rotation of due north in relation to the top of the view in degrees.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-Viewpoint.html#rotation)
     */
    @JsonProperty()
    declare rotation?: number;
    /**
     * The scale of the viewpoint.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-Viewpoint.html#scale)
     */
    @JsonProperty()
    declare scale?: number;
    /**
     * The target geometry framed by the viewpoint.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-Viewpoint.html#targetGeometry)
     */
    @JsonProperty()
    declare targetGeometry?: GeometryConfiguration;

    constructor() {}
}
@JsonObject()
export class HighlightOptionsConfiguration {
    /**
     * The color of the highlight fill.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html#HighlightOptions)
     */
    @JsonProperty()
    declare color?: ColorConfiguration | number[] | string;

    /**
     * The color of the halo surrounding the highlight.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html#HighlightOptions)
     */
    @JsonProperty()
    declare haloColor?: ColorConfiguration | number[] | string;

    /**
     * The opacity of the highlight halo.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html#HighlightOptions)
     */
    @JsonProperty()
    declare haloOpacity?: number;

    /**
     * The opacity of the fill (area within the halo).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html#HighlightOptions)
     */
    @JsonProperty()
    declare fillOpacity?: number;

    /**
     * The color of the highlighted feature's shadow in [3D SceneView](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-SceneView.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html#HighlightOptions)
     */
    @JsonProperty()
    declare shadowColor?: ColorConfiguration;
    /**
     * The opacity of the highlighted feature's shadow.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html#HighlightOptions)
     */
    @JsonProperty()
    declare shadowOpacity?: number;
    /**
     * Defines the intensity of the shadow area obtained by overlapping the shadow of the highlighted feature and the shadow of other objects in [3D SceneView](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-SceneView.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-View.html#HighlightOptions)
     */
    @JsonProperty()
    declare shadowDifference?: number;

    constructor() {}
}
@JsonObject()
export class MapViewConfiguration extends ViewConfiguration {
    /**
     * The background color of the MapView.
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#background)
     */
    @JsonProperty()
    declare background: ColorBackgroundConfiguration;
    /**
     * Represents the view's center point; when setting the center, you may pass a [Point](https://developers.arcgis.com/javascript/latest/api-reference/esri-geometry-Point.html) instance or an array of numbers representing a longitude/latitude pair (`[-100.4593, 36.9014]`).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#center)
     */
    @JsonProperty()
    declare center?: (PointConfiguration & { type: "point" }) | number[];
    /**
     * Specifies constraints to [scale](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#scale), [zoom](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#zoom), and [rotation](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#rotation) that may be applied to the MapView.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#constraints)
     */
    @JsonProperty()
    declare constraints: MapViewConstraintsConfiguration;
    /**
     * The extent represents the visible portion of a [map](https://developers.arcgis.com/javascript/latest/api-reference/esri-Map.html) within the view as an instance of [Extent](https://developers.arcgis.com/javascript/latest/api-reference/esri-geometry-Extent.html).
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#extent)
     */
    @JsonProperty()
    declare extent: ExtentConfiguration;
    /**
     * Applies a display filter on the view for a specific set of floor levels.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#floors)
     */
    @JsonProperty()
    declare floors?: Collection<string>;
    /**
     * Options for configuring the highlight.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#highlightOptions)
     */
    @JsonProperty()
    declare highlightOptions: HighlightOptionsConfiguration;
    /**
     * Defines which anchor stays still while resizing the browser window.
     *
     * @default center
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#resizeAlign)
     */
    @JsonProperty()
    declare resizeAlign:
    | "center"
    | "left"
    | "right"
    | "top"
    | "bottom"
    | "top-left"
    | "top-right"
    | "bottom-left"
    | "bottom-right";
    /**
     * The clockwise rotation of due north in relation to the top of the view in degrees.
     *
     * @default 0
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#rotation)
     */
    @JsonProperty()
    declare rotation: number;
    /**
     * Represents the map scale at the center of the view.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#scale)
     */
    @JsonProperty()
    declare scale?: number;
    /**
     * Indicates if the MapView's [spatialReference](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#spatialReference) can be changed after it is initialized.
     *
     * @default false
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#spatialReferenceLocked)
     */
    @JsonProperty()
    declare spatialReferenceLocked?: boolean;
    /**
     * Defines the time zone of the view.
     *
     * @default system
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#timeZone)
     */
    @JsonProperty()
    declare timeZone?: string;
    /**
     * The dimension of the view.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#type)
     */
    declare type: "2d";
    /**
     * Represents the current view as a [Viewpoint](https://developers.arcgis.com/javascript/latest/api-reference/esri-Viewpoint.html) or point of observation on the view.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#viewpoint)
     */
    @JsonProperty()
    declare viewpoint?: ViewpointConfiguration;
    /**
     * Represents the level of detail (LOD) at the center of the view.
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-MapView.html#zoom)
     */
    @JsonProperty()
    declare zoom?: number;

    /** --- from PopupViewProperties --- */
    /**
     * A [Popup](https://developers.arcgis.com/javascript/latest/api-reference/esri-widgets-Popup.html) object that displays general content or attributes from [layers](https://developers.arcgis.com/javascript/latest/api-reference/esri-Map.html#layers) in the [map](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-PopupView.html#map).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-PopupView.html#popup)
     */
    @JsonProperty()
    declare popup?: PopupConfiguration;
    /**
     * Controls whether the popup opens when users click on the view.
     *
     * @default true
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-views-PopupView.html#popupEnabled)
     */
    @JsonProperty()
    declare popupEnabled?: boolean;
    /** --- end from PopupViewProperties --- */

    constructor() {
        super();
    }
}

@JsonObject()
export class InitialViewConfiguration {
    /**
     * The background color of the [WebMap](https://developers.arcgis.com/javascript/latest/api-reference/esri-WebMap.html).
     *
     * @default null
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-webmap-InitialViewProperties.html#background)
     */
    @JsonProperty()
    declare background: ColorBackgroundConfiguration;
    /**
     * The spatial reference of the [WebMap](https://developers.arcgis.com/javascript/latest/api-reference/esri-WebMap.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-webmap-InitialViewProperties.html#spatialReference)
     */
    @JsonProperty()
    declare spatialReference: SpatialReferenceConfiguration;

    /**
     * The initial time zone of the [WebMap](https://developers.arcgis.com/javascript/latest/api-reference/esri-WebMap.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-webmap-InitialViewProperties.html#timeZone)
     */
    @JsonProperty()
    declare timeZone: string;

    /**
     * The initial viewpoint of the [WebMap](https://developers.arcgis.com/javascript/latest/api-reference/esri-WebMap.html).
     *
     * [Read more...](https://developers.arcgis.com/javascript/latest/api-reference/esri-webmap-InitialViewProperties.html#viewpoint)
     */
    @JsonProperty()
    declare viewpoint: ViewpointConfiguration;

    constructor() {}
}
